import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { GoldIcon, SaveIcon, CloseIcon, CheckIcon, ArrowIcon } from './BespokeIcons';
import { toast } from 'sonner';

/**
 * GoldenSummary - Beautiful comprehensive summary with download & save options
 * Can summarize: Belonging journey, Vision quest, or complete journey
 */
export const GoldenSummary = ({ 
  isOpen, 
  onClose,
  belongingData = null,  // OFNR journey data
  visionData = null,     // Vision quest data
  includeVision = false,
  userId = null
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  
  const generateNarrative = () => {
    const sections = [];
    
    // Belonging Journey Section
    if (belongingData) {
      const { title, participants, gold, isAppreciation, isRomance } = belongingData;
      
      sections.push(`# ${title || 'The Journey'}`);
      sections.push('');
      sections.push(isRomance ? '*A journey of intimate connection*' : isAppreciation ? '*A celebration of gratitude*' : '*A path from friction to gold*');
      sections.push('');
      sections.push('---');
      sections.push('');
      
      // Each participant's journey
      if (participants && participants.length > 0) {
        participants.forEach((p, idx) => {
          if (p.observation || p.feelings || p.needs) {
            sections.push(`## ${p.name || 'Voice ' + (idx + 1)}`);
            sections.push('');
            
            if (p.observation) {
              sections.push('**What Was Witnessed** (Blue)');
              sections.push(`> ${p.observation}`);
              sections.push('');
            }
            
            if (p.feelings) {
              sections.push('**Feelings Alive** (Red)');
              sections.push(`> ${p.feelings}`);
              sections.push('');
            }
            
            if (p.needs) {
              sections.push('**Needs at the Heart** (Green)');
              sections.push(`> ${p.needs}`);
              sections.push('');
            }
            
            // Thank You (appreciation mode)
            if (p.appreciationObservation || p.appreciationFeeling || p.appreciationNeedMet) {
              sections.push('**Thank You**');
              if (p.appreciationObservation) sections.push(`- What you did: ${p.appreciationObservation}`);
              if (p.appreciationFeeling) sections.push(`- What I feel: ${p.appreciationFeeling}`);
              if (p.appreciationNeedMet) sections.push(`- Need that was met: ${p.appreciationNeedMet}`);
              sections.push('');
            }
            
            sections.push('---');
            sections.push('');
          }
        });
        
        // Empathy Bridges
        const hasEmpathy = participants.some(p => p.empathyGuesses && Object.keys(p.empathyGuesses).length > 0);
        if (hasEmpathy) {
          sections.push('## Bridges of Empathy');
          sections.push('');
          sections.push('*How we tried to understand each other...*');
          sections.push('');
          
          participants.forEach(p => {
            if (p.empathyGuesses) {
              Object.entries(p.empathyGuesses).forEach(([targetId, guesses]) => {
                if (targetId === p.id) return; // Skip self-empathy
                
                const targetName = participants.find(t => t.id === targetId)?.name || 'the other';
                if (guesses.red || guesses.green) {
                  sections.push(`**${p.name}** wondered about **${targetName}**:`);
                  if (guesses.red) sections.push(`- Perhaps feeling: ${guesses.red}`);
                  if (guesses.green) sections.push(`- Maybe needing: ${guesses.green}`);
                  sections.push('');
                }
              });
            }
          });
          
          sections.push('---');
          sections.push('');
        }
      }
      
      // Gold Resolution
      if (gold) {
        sections.push('## ' + (isRomance ? 'Our Connection' : isAppreciation ? 'Celebration' : 'The Golden Thread'));
        sections.push('');
        sections.push(`> ${gold}`);
        sections.push('');
        sections.push('---');
        sections.push('');
      }
    }
    
    // Vision Quest Section
    if (visionData && includeVision) {
      const { title, values, purpose, bhag, story } = visionData;
      
      sections.push('# Vision Quest');
      sections.push('');
      sections.push('*From understanding to purpose...*');
      sections.push('');
      
      if (title) {
        sections.push(`## ${title}`);
        sections.push('');
      }
      
      if (values && values.length > 0) {
        sections.push('**Core Values**');
        sections.push(values.map(v => `- ${v}`).join('\n'));
        sections.push('');
      }
      
      if (purpose) {
        sections.push('**Purpose**');
        sections.push(`> ${purpose}`);
        sections.push('');
      }
      
      if (bhag) {
        sections.push('**Big Hairy Audacious Goal**');
        sections.push(`> ${bhag}`);
        sections.push('');
      }
      
      if (story) {
        sections.push('**The Story**');
        sections.push('');
        sections.push(story);
        sections.push('');
      }
    }
    
    return sections.join('\n');
  };
  
  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    
    try {
      const narrative = generateNarrative();
      
      // Create printable HTML
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>ElchmE Summary</title>
          <style>
            body {
              font-family: 'Playfair Display', Georgia, serif;
              max-width: 800px;
              margin: 40px auto;
              padding: 20px;
              line-height: 1.8;
              color: #1a1a1a;
            }
            h1 { color: #D97706; border-bottom: 2px solid #FCD34D; padding-bottom: 10px; }
            h2 { color: #059669; margin-top: 30px; }
            blockquote {
              border-left: 4px solid #FBBF24;
              padding-left: 20px;
              margin: 20px 0;
              font-style: italic;
              color: #4B5563;
            }
            hr { border: none; border-top: 1px solid #E5E7EB; margin: 30px 0; }
            strong { color: #DC2626; }
          </style>
        </head>
        <body>
          ${narrative.split('\n').map(line => {
            if (line.startsWith('# ')) return `<h1>${line.substring(2)}</h1>`;
            if (line.startsWith('## ')) return `<h2>${line.substring(3)}</h2>`;
            if (line.startsWith('> ')) return `<blockquote>${line.substring(2)}</blockquote>`;
            if (line.startsWith('**') && line.endsWith('**')) return `<strong>${line.replace(/\*\*/g, '')}</strong>`;
            if (line.startsWith('- ')) return `<li>${line.substring(2)}</li>`;
            if (line === '---') return '<hr/>';
            if (line.startsWith('*') && line.endsWith('*')) return `<p style="font-style: italic; color: #6B7280;">${line.replace(/\*/g, '')}</p>`;
            return line ? `<p>${line}</p>` : '';
          }).join('\n')}
          <script>window.print();</script>
        </body>
        </html>
      `);
      printWindow.document.close();
      
      toast.success('Opening print dialog...');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };
  
  const handleSaveToDashboard = async () => {
    if (!userId) {
      toast.error('Please sign in to save to your dashboard');
      return;
    }
    
    setIsSaving(true);
    
    try {
      const narrative = generateNarrative();
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/summaries/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          belongingData,
          visionData: includeVision ? visionData : null,
          narrative,
          createdAt: new Date().toISOString()
        })
      });
      
      if (response.ok) {
        toast.success('✅ Saved to your dashboard!');
      } else {
        toast.error('Failed to save. Please try again.');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  const narrative = generateNarrative();
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gradient-to-br from-zinc-900 via-amber-900/20 to-zinc-900 border-amber-500/30 text-white max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-3xl text-amber-400 flex items-center gap-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            <div className="relative">
              <div className="absolute inset-0 bg-amber-500/30 rounded-full animate-ping" />
              <GoldIcon size={32} color="#FBBF24" className="relative z-10" />
            </div>
            Golden Summary
          </DialogTitle>
        </DialogHeader>
        
        {/* Summary Content - Scrollable */}
        <div className="flex-1 overflow-y-auto pr-2 space-y-4">
          <div className="prose prose-invert max-w-none">
            {narrative.split('\n').map((line, idx) => {
              if (line.startsWith('# ')) {
                return <h1 key={idx} className="text-2xl text-amber-400 mb-4 mt-6" style={{ fontFamily: "'Playfair Display', serif" }}>{line.substring(2)}</h1>;
              }
              if (line.startsWith('## ')) {
                return <h2 key={idx} className="text-xl text-emerald-400 mb-3 mt-5 font-medium">{line.substring(3)}</h2>;
              }
              if (line.startsWith('> ')) {
                return (
                  <blockquote key={idx} className="border-l-4 border-amber-500/50 pl-4 my-4 text-white/80 italic">
                    {line.substring(2)}
                  </blockquote>
                );
              }
              if (line.startsWith('**') && line.endsWith('**')) {
                return <p key={idx} className="text-rose-300 font-semibold mt-3">{line.replace(/\*\*/g, '')}</p>;
              }
              if (line.startsWith('- ')) {
                return <li key={idx} className="text-white/70 ml-4">{line.substring(2)}</li>;
              }
              if (line === '---') {
                return <hr key={idx} className="border-amber-500/20 my-6" />;
              }
              if (line.startsWith('*') && line.endsWith('*')) {
                return <p key={idx} className="text-amber-200/60 italic text-sm text-center">{line.replace(/\*/g, '')}</p>;
              }
              return line ? <p key={idx} className="text-white/80">{line}</p> : <div key={idx} className="h-2" />;
            })}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-amber-500/20">
          <Button
            onClick={handleDownloadPDF}
            disabled={isDownloading}
            className="flex-1 bg-gradient-to-r from-amber-500/20 to-orange-500/20 hover:from-amber-500/30 hover:to-orange-500/30 text-amber-300 border border-amber-500/30"
          >
            {isDownloading ? (
              <>
                <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin mr-2" />
                Preparing...
              </>
            ) : (
              <>
                <ArrowIcon size={18} color="#FCD34D" direction="down" className="mr-2" />
                Download as PDF
              </>
            )}
          </Button>
          
          {userId && (
            <Button
              onClick={handleSaveToDashboard}
              disabled={isSaving}
              className="flex-1 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 hover:from-emerald-500/30 hover:to-teal-500/30 text-emerald-300 border border-emerald-500/30"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <SaveIcon size={18} color="#10B981" className="mr-2" />
                  Save to Dashboard
                </>
              )}
            </Button>
          )}
          
          <Button
            onClick={onClose}
            variant="ghost"
            className="text-white/50 hover:text-white"
          >
            <CloseIcon size={18} color="currentColor" className="mr-2" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * GoldenSummaryButton - Trigger button with gold orb animation
 */
export const GoldenSummaryButton = ({ onClick, label = "Golden Summary" }) => {
  return (
    <button
      onClick={onClick}
      className="w-full py-4 px-6 rounded-xl bg-gradient-to-br from-amber-900/30 to-yellow-900/20 hover:from-amber-900/40 hover:to-yellow-900/30 border-2 border-amber-500/40 hover:border-amber-400/60 transition-all group relative overflow-hidden"
      data-testid="golden-summary-btn"
    >
      {/* Animated background glow */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-500/10 to-transparent animate-shimmer" />
      
      <div className="relative z-10 flex items-center justify-center gap-3">
        {/* Pulsing golden orb */}
        <div className="relative">
          <div className="absolute inset-0 bg-amber-500/30 rounded-full animate-ping" />
          <div className="absolute inset-0 bg-amber-500/40 rounded-full" style={{ animation: 'pulse 2s ease-in-out infinite' }} />
          <GoldIcon size={28} color="#FBBF24" className="relative z-10" style={{ filter: 'drop-shadow(0 0 12px #FBBF24)' }} />
        </div>
        
        <span className="text-amber-300 font-medium text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
          {label}
        </span>
      </div>
      
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </button>
  );
};

export default GoldenSummary;
